import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import Spinner from "components/base/Spinner";
import PageV2 from "components/shared/PageV2";
import { useModals } from "ModalProvider";
import { useParams, useSearchParams } from "react-router-dom";
import CustomerReportSettingsForm from "./CustomerReportSettingsForm";
import { FETCH_CUSTOMER_INFO_FOR_SALES_REPORT } from "./graphql";
import ReportView from "./ReportView";

const CustomerSalesReportPage = () => {
  const { partnerId } = useParams();
  const modal = useModals();

  const { loading, error, data } = useQuery(
    FETCH_CUSTOMER_INFO_FOR_SALES_REPORT,
    {
      variables: {
        instance: "AB",
        useType: "SALES_REPORT",
        odooPartnerIds: [partnerId],
        odooPartnerId: partnerId,
      },
    },
  );

  const currentYear = new Date().getFullYear();
  const defaultStartYear = currentYear - 2;
  const defaultEndYear = currentYear;
  const [searchParams, setSearchParams] = useSearchParams({
    startYear: String(defaultStartYear),
    endYear: String(defaultEndYear),
    currency: "USD",
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const params = Object.fromEntries([...searchParams]);
  const startYear = Number(searchParams.get("startYear")) || defaultStartYear;
  const endYear = Number(searchParams.get("endYear")) || defaultEndYear;
  const currency = searchParams.get("currency") || "USD";
  const yearOptions = Array.from({ length: 9 }, (_, i) => currentYear - 4 + i);
  const customer = data.customer || {
    id: null,
    name: "Unknown Customer",
    reportData: null,
  };

  function showCustomerReportSettings() {
    modal.present({
      title: "Customer Report Settings",
      subtitle: customer.name,
      children: (
        <CustomerReportSettingsForm
          id={customer.id}
          originalReportData={customer.reportData}
          hide={modal.hide}
        />
      ),
    });
  }

  return (
    <PageV2
      testing
      title={`Sales Report: ${customer.name}`}
      backTo="./.."
      toolbar={
        <div>
          <Button onClick={showCustomerReportSettings}>
            Customer Report Settings
          </Button>
        </div>
      }
    >
      <div>
        <div className="flex items-center gap-4 justify-between">
          <div className="flex items-center gap-2">
            <Select
              value={startYear}
              onChange={(e) => {
                const nextStartYear = Number(e.target.value);
                setSearchParams({
                  ...params,
                  startYear: String(nextStartYear),
                  endYear: String(Math.max(nextStartYear, endYear)),
                });
              }}
            >
              {yearOptions.map((year) => (
                <option key={`start-${year}`} value={year}>
                  {year}
                </option>
              ))}
            </Select>
            <span className="opacity-60">to</span>
            <Select
              value={endYear}
              onChange={(e) => {
                const nextEndYear = Number(e.target.value);
                setSearchParams({
                  ...params,
                  startYear: String(Math.min(startYear, nextEndYear)),
                  endYear: String(nextEndYear),
                });
              }}
            >
              {yearOptions.map((year) => (
                <option key={`end-${year}`} value={year}>
                  {year}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <Select
              value={currency}
              onChange={(e) => {
                setSearchParams({ ...params, currency: e.target.value });
              }}
            >
              <option>USD</option>
              <option>SEK</option>
              <option>EUR</option>
              <option>CNY</option>
            </Select>
          </div>
        </div>
      </div>

      <div className="relative flex-1">
        <ReportView
          startYear={startYear}
          endYear={endYear}
          currency={currency}
          partnerId={partnerId}
          customerData={data}
        />
      </div>
    </PageV2>
  );
};

export default CustomerSalesReportPage;
